import { JSX, useEffect, useRef, useState } from 'react';
import {
  Field,
  LinkField,
  ImageField,
  Image,
} from '@sitecore-jss/sitecore-jss-nextjs';
import axios from 'axios';
import Icons from 'components/atoms/Icons';
import constant from 'assets/utils/constant';
import { formatLocationUrl } from 'assets/utils/formatUrl';
import { LocationType } from 'components/types/LocationTypes';
import { LocationFields as DynamicLocationFields } from 'components/types/MapTypes';
import { ItemValue } from 'components/types/ListItem';
import Modal from 'assets/shared/Modal';
import { StyleguideComponentProps } from 'lib/component-props';
import { classNames } from 'assets/utils/helper';
import { getMarginSize } from 'assets/utils/getMarginSize';

type Slot = {
  Time: string;
};

type LatLng = {
  lat: number;
  lng: number;
};

type AzureSuggestion = {
  id: string;
  label: string;
  address: {
    label?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
};

const AZURE_MAPS_ENDPOINT = 'https://atlas.microsoft.com';

const getAzureMapsKey = () =>
  process.env.NEXT_PUBLIC_AZURE_MAPS_SUBSCRIPTION_KEY;

const getDistance = (
  originLat: number,
  originLon: number,
  latitude: number,
  longitude: number
) => {
  const EarthMeanRadiusInKilometers = 6371.0087714;
  const phi1 = (originLat * Math.PI) / 180;
  const phi2 = (latitude * Math.PI) / 180;
  const deltaPhi = ((latitude - originLat) * Math.PI) / 180;
  const deltaLambda = ((longitude - originLon) * Math.PI) / 180;

  const a =
    Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) *
      Math.cos(phi2) *
      Math.sin(deltaLambda / 2) *
      Math.sin(deltaLambda / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distanceInMiles = EarthMeanRadiusInKilometers * c * 0.621371192;

  return distanceInMiles;
};

type LocationCalloutProps = StyleguideComponentProps & {
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    Title: Field<string>;
    'View More CTA': LinkField;
    'Page Image': ImageField;
    Locations: Array<DynamicLocationFields>;
    Keywords: Field<string>;
    LocationTypes: Array<LocationType>;
    ZipCode: Field<string>;
    Radius: ItemValue;
  };
};

const buildLocationString = (address: AzureSuggestion['address']) => {
  return (
    address.label ||
    `${address.city ? `${address.city}, ` : ''}${
      address.state ? `${address.state} ` : ''
    }${address.zip ?? ''}`.trim()
  );
};

const isIllinoisSuggestion = (suggestion: AzureSuggestion): boolean => {
  const state = suggestion.address.state?.trim().toLowerCase() || '';
  const label = suggestion.label?.trim().toLowerCase() || '';

  return (
    state === 'il' ||
    state === 'illinois' ||
    label.includes(', il') ||
    label.includes(', illinois') ||
    label.endsWith(' il') ||
    label.endsWith(' illinois')
  );
};

const LocationCallout = (props: LocationCalloutProps): JSX.Element => {
  const marginSize = getMarginSize(
    props?.fields?.MarginSize?.fields?.heading?.value
  );

  const INITIAL_DISPLAY_COUNT = 3;
  const DEFAULT_DISTANCE = 50;

  const radiusInMiles =
    parseFloat(props.fields.Radius?.fields?.heading?.value) || DEFAULT_DISTANCE;

  const [inputLocation, setInputLocation] = useState('');
  const [coords, setCoords] = useState<LatLng | null>();
  const [location, setLocation] = useState('');
  const [geolocating, setGeolocating] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState<
    Array<DynamicLocationFields>
  >([]);
  const [waitTimes, setWaitTimes] = useState<{ [key: string]: string }>({});
  const [nextAppts, setNextAppts] = useState<{ [key: string]: string }>({});
  const [suggestions, setSuggestions] = useState<Array<AzureSuggestion>>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suggestionRef = useRef<AzureSuggestion | null>(null);

  const locations = props.fields.Locations || [];
  const locationsToDisplay = filteredLocations.slice(0, INITIAL_DISPLAY_COUNT);

  const fetchLocationData = async (
    deptId: string,
    providerId: string,
    locationId: string,
    locationName: string
  ) => {
    const shouldFetchWaitTimes =
      locationName.includes('OnCall Urgent') ||
      locationName.includes('PromptCare');

    if (!shouldFetchWaitTimes || !deptId) {
      return;
    }

    try {
      const waitTimesResponse = await axios.post(
        constant.api.epicApi + constant.api.getDepartmentWaitTimes,
        {
          Departments: [{ ID: deptId, Type: 'External' }],
          MinutesToLookBack_: 60,
          UseAppointmentTimeIfAfterStartEvent_: true,
          CalculateForOverallLevel_: false,
          CalculateForEachDepartment_: false,
          StartEvent: { ID: '', Type: '' },
          EndEvent: { ID: '', Type: '' },
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Epic-Client-ID': constant.api.epicClientID,
            Accept: 'application/json',
            Authorization: `Basic ${constant.api.epicAccessToken}`,
          },
        }
      );

      const waitTimeSeconds = waitTimesResponse.data.AverageWaitTime;
      const waitTimeMinutes = Math.floor(waitTimeSeconds / 60);
      const waitTimeHours = Math.floor(waitTimeMinutes / 60);
      const remainingMinutes = waitTimeMinutes % 60;

      let formattedWaitTime = '';

      if (waitTimeHours > 0) {
        formattedWaitTime = `${waitTimeHours} Hour${
          waitTimeHours > 1 ? 's' : ''
        } and ${remainingMinutes} Minute${
          remainingMinutes > 1 ? 's' : ''
        }`;
      } else {
        formattedWaitTime = `${waitTimeMinutes} Minute${
          waitTimeMinutes > 1 ? 's' : ''
        }`;
      }

      setWaitTimes((prev) => ({ ...prev, [locationId]: formattedWaitTime }));

      if (!locationName.includes('PromptCare') && providerId) {
        const today = new Date().toISOString().split('T')[0];
        const tomorrow = new Date(Date.now() + 86400000)
          .toISOString()
          .split('T')[0];

        const nextAppointmentResponse = await axios.post(
          constant.api.epicApi + constant.api.getScheduleDays,
          {
            StartDate: today,
            EndDate: tomorrow,
            ProviderID: providerId,
            ProviderIDType: 'External',
            VisitTypeIDs: [{ ID: '24692', Type: 'External' }],
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Epic-Client-ID': constant.api.epicClientID,
              Accept: 'application/json',
              Authorization: `Basic ${constant.api.epicAccessToken}`,
            },
          }
        );

        const scheduleDays = nextAppointmentResponse.data.ScheduleDays;
        let nextAppointment: Slot | null = null;
        let nextAppointmentDate: string | null = null;

        for (const day of scheduleDays) {
          if (day.Slots.length > 0) {
            nextAppointment = day.Slots[0];
            nextAppointmentDate = day.Date;
            break;
          }
        }

        if (nextAppointment && nextAppointmentDate) {
          const appointmentTime = new Date(
            `${nextAppointmentDate}T${nextAppointment.Time}`
          );
          const hours = appointmentTime.getHours();
          const minutes = appointmentTime.getMinutes();
          const ampm = hours >= 12 ? 'p.m.' : 'a.m.';
          const formattedHours = hours % 12 || 12;
          const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
          const formattedDate =
            nextAppointmentDate === today ? 'Today' : 'Tomorrow';

          const formattedAppt = `${formattedDate} at ${formattedHours}:${formattedMinutes} ${ampm}`;

          setNextAppts((prev) => ({ ...prev, [locationId]: formattedAppt }));
        } else {
          setNextAppts((prev) => ({
            ...prev,
            [locationId]: 'No upcoming appointments',
          }));
        }
      }
    } catch (error) {
      console.error(`Error fetching data for ${locationId}:`, error);
    }
  };

  const fetchAzureSuggestions = async (query: string) => {
    const subscriptionKey = getAzureMapsKey();
    const trimmedQuery = query.trim();

    if (!subscriptionKey || !trimmedQuery || trimmedQuery.length <= 1) {
      setSuggestions([]);
      suggestionRef.current = null;
      return;
    }

    try {
      setIsLoadingSuggestions(true);

      const response = await axios.get(
        `${AZURE_MAPS_ENDPOINT}/search/fuzzy/json`,
        {
          params: {
            'api-version': '1.0',
            query: trimmedQuery,
            countrySet: 'US',
            limit: 15,
            typeahead: true,
            'subscription-key': subscriptionKey,
          },
          headers: {
            'Accept-Language': 'en-US',
          },
        }
      );

      const results = Array.isArray(response.data?.results)
        ? response.data.results
        : [];

      const normalizedSuggestions = results
        .map((result: any) => {
          const address = result.address;

          const city = address?.municipality;
          const state = address?.countrySubdivision;
          const zip = address?.postalCode;

          const label =
            address?.freeformAddress ||
            result.poi?.name ||
            [city, state, zip].filter(Boolean).join(', ');

          return {
            id: `${label}-${result.type ?? ''}`,
            label,
            address: {
              label,
              city,
              state,
              zip,
            },
          };
        })
        .filter((suggestion: AzureSuggestion) => suggestion.label);

      const uniqueSuggestions = normalizedSuggestions.filter(
        (
          suggestion: AzureSuggestion,
          index: number,
          self: AzureSuggestion[]
        ) => index === self.findIndex((item) => item.label === suggestion.label)
      );

      const illinoisSuggestions = uniqueSuggestions.filter(isIllinoisSuggestion);

      const otherSuggestions = uniqueSuggestions.filter(
        (suggestion: AzureSuggestion) => !isIllinoisSuggestion(suggestion)
      );

      const prioritizedSuggestions = [
        ...illinoisSuggestions,
        ...otherSuggestions,
      ];

      setSuggestions(prioritizedSuggestions.slice(0, 5));
      suggestionRef.current = prioritizedSuggestions[0] || null;
    } catch (error: any) {
      console.error('Azure fuzzy search error:', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });

      setSuggestions([]);
      suggestionRef.current = null;
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const geocodeSelectedSuggestion = async (suggestion: AzureSuggestion) => {
    const subscriptionKey = getAzureMapsKey();

    if (!subscriptionKey) {
      return;
    }

    try {
      const response = await axios.get(
        `${AZURE_MAPS_ENDPOINT}/search/address/json`,
        {
          params: {
            'api-version': '1.0',
            query: suggestion.label,
            countrySet: 'US',
            limit: 1,
            'subscription-key': subscriptionKey,
          },
          headers: {
            'Accept-Language': 'en-US',
          },
        }
      );

      const result = response.data?.results?.[0];

      if (!result?.position?.lat || !result?.position?.lon) {
        return;
      }

      const latLng: LatLng = {
        lat: result.position.lat,
        lng: result.position.lon,
      };

      const address = {
        label: suggestion.label,
        city: suggestion.address.city || result?.address?.municipality,
        state: suggestion.address.state || result?.address?.countrySubdivision,
        zip: suggestion.address.zip || result?.address?.postalCode,
      };

      const locationString = buildLocationString(address);

      setLocation(locationString);
      setInputLocation(locationString);
      setCoords(latLng);
      setSuggestions([]);
    } catch (error: any) {
      console.error('Azure selected suggestion geocode error:', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });

      setSuggestions([]);
    }
  };

  const reverseGeocode = async (latLng: LatLng) => {
    const subscriptionKey = getAzureMapsKey();

    if (!subscriptionKey) {
      setGeolocating(false);
      return;
    }

    try {
      const response = await axios.get(
        `${AZURE_MAPS_ENDPOINT}/reverseGeocode`,
        {
          params: {
            'api-version': '2026-01-01',
            coordinates: `${latLng.lng},${latLng.lat}`,
            'subscription-key': subscriptionKey,
            language: 'en-US',
          },
        }
      );

      const azureAddress = response.data?.features?.[0]?.properties?.address;

      const address = {
        city: azureAddress?.locality || azureAddress?.municipality,
        state:
          azureAddress?.adminDistricts?.[0]?.shortName ||
          azureAddress?.adminDistricts?.[0]?.name,
        zip: azureAddress?.postalCode,
      };

      const locationString = buildLocationString(address);

      setLocation(locationString);
      setInputLocation(locationString);
      setGeolocating(false);
    } catch (error: any) {
      console.error('Azure reverse geocode error:', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });

      setGeolocating(false);
    }
  };

  const handleChange = (address: string) => {
    setInputLocation(address);
  };

  const handleSelect = async (suggestion?: AzureSuggestion) => {
    const selectedSuggestion = suggestion || suggestionRef.current;

    const outerModal = document.querySelector('.pdc-modal') as HTMLElement;
    outerModal && outerModal.click();

    if (!selectedSuggestion) {
      return;
    }

    await geocodeSelectedSuggestion(selectedSuggestion);
  };

  const useCurrentLoc = () => {
    setGeolocating(true);
    setInputLocation('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (position?.coords?.latitude && position?.coords?.longitude) {
          const latLng: LatLng = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          setCoords(latLng);
          reverseGeocode(latLng);
        }
      },
      () => {
        setCoords(null);
        setLocation('');
        setInputLocation('');
        setGeolocating(false);
      }
    );

    const outerModal = document.querySelector('.pdc-modal') as HTMLElement;
    outerModal && outerModal.click();
  };

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const trimmedLocation = inputLocation.trim();

    if (!trimmedLocation || trimmedLocation.length <= 1) {
      setSuggestions([]);
      suggestionRef.current = null;
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchAzureSuggestions(trimmedLocation);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [inputLocation]);

  useEffect(() => {
    filteredLocations.forEach((loc: any, index) => {
      const locationName =
        loc.fields?.LocationName?.value ||
        loc.displayName ||
        loc.locationname ||
        'Unknown Location';

      const deptId = loc.fields?.EpicDeptId?.value;
      const providerId = loc.fields?.EpicProviderId?.value;
      const locationId =
        loc.fields?.LocationId?.value || loc.id || `loc-${index}`;

      if (
        deptId &&
        (locationName.includes('OnCall Urgent') ||
          locationName.includes('PromptCare'))
      ) {
        fetchLocationData(deptId, providerId, locationId, locationName);
      }
    });
  }, [filteredLocations]);

  useEffect(() => {
    let filtered = [...locations];

    if (
      props.fields.Keywords?.value &&
      props.fields.Keywords.value.trim().length > 0
    ) {
      const keywords = props.fields.Keywords.value
        .toLowerCase()
        .split(',')
        .map((k) => k.trim())
        .filter((k) => k.length > 0);

      if (keywords.length > 0) {
        filtered = filtered.filter((loc: any) => {
          const locationName = (
            loc.fields?.LocationName?.value ||
            loc.displayName ||
            loc.locationname ||
            ''
          ).toLowerCase();

          const city = (
            loc.fields?.City?.value ||
            loc.city ||
            ''
          ).toLowerCase();

          const state = (
            loc.fields?.State?.value ||
            loc.state ||
            ''
          ).toLowerCase();

          const street = (
            loc.fields?.Street1?.value ||
            loc.street1 ||
            ''
          ).toLowerCase();

          const searchableText = `${locationName} ${city} ${state} ${street}`;

          return keywords.some((keyword) => searchableText.includes(keyword));
        });
      }
    }

    if (props.fields.LocationTypes && props.fields.LocationTypes.length > 0) {
      const selectedTypes = props.fields.LocationTypes.map((type) =>
        (type.displayName || type.name || '').toLowerCase()
      );

      filtered = filtered.filter((loc: any) => {
        const locationTypesField = (
          loc.fields?.LocationTypes?.value ||
          loc.locationtypes ||
          ''
        ).toLowerCase();

        if (locationTypesField.length > 0) {
          return selectedTypes.some((type) => locationTypesField.includes(type));
        }

        const locationName = (
          loc.fields?.LocationName?.value ||
          loc.displayName ||
          loc.locationname ||
          ''
        ).toLowerCase();

        return selectedTypes.some((type) =>
          locationName.includes(type.toLowerCase())
        );
      });
    }

    if (!coords || !coords.lat || !coords.lng) {
      setFilteredLocations(filtered);
      return;
    }

    const locationsWithDistance = filtered
      .map((loc: any) => {
        const lat = loc.fields?.Latitude?.value || loc.latitude;
        const lng = loc.fields?.Longitude?.value || loc.longitude;

        if (!lat || !lng) return null;

        const distance = getDistance(
          coords.lat,
          coords.lng,
          parseFloat(lat),
          parseFloat(lng)
        );

        return { ...loc, calculatedDistance: distance };
      })
      .filter((loc) => loc !== null) as Array<
      DynamicLocationFields & { calculatedDistance: number }
    >;

    const withinRadius = locationsWithDistance.filter(
      (loc: any) => loc.calculatedDistance <= radiusInMiles
    );

    const sorted = withinRadius.sort(
      (a: any, b: any) => a.calculatedDistance - b.calculatedDistance
    );

    setFilteredLocations(sorted);
  }, [
    coords,
    locations,
    radiusInMiles,
    props.fields.Keywords,
    props.fields.LocationTypes,
  ]);

  useEffect(() => {
    if (props.fields.ZipCode?.value) {
      const setZipCoords = async () => {
        const subscriptionKey = getAzureMapsKey();

        if (!subscriptionKey) {
          return;
        }

        try {
          const response = await axios.get(
            `${AZURE_MAPS_ENDPOINT}/search/address/json`,
            {
              params: {
                'api-version': '1.0',
                query: props.fields.ZipCode.value,
                countrySet: 'US',
                limit: 1,
                'subscription-key': subscriptionKey,
              },
              headers: {
                'Accept-Language': 'en-US',
              },
            }
          );

          const result = response.data?.results?.[0];

          if (!result?.position?.lat || !result?.position?.lon) {
            return;
          }

          const coordinates: LatLng = {
            lat: result.position.lat,
            lng: result.position.lon,
          };

          const address = {
            city: result?.address?.municipality,
            state: result?.address?.countrySubdivision,
            zip: result?.address?.postalCode || props.fields.ZipCode.value,
          };

          setCoords(coordinates);
          setLocation(buildLocationString(address));
          setInputLocation(buildLocationString(address));
        } catch (error: any) {
          console.error('Azure geolocation error:', {
            message: error?.message,
            status: error?.response?.status,
            data: error?.response?.data,
          });
        }
      };

      setZipCoords();
    }
  }, []);

  return (
    <div
      className={classNames('overhauled location-callout', marginSize)}
      key={props.rendering.uid}
    >
      <div className="jss-inner-container">
        <div className="header-wrapper">
          {props.fields.Title?.value && (
            <h2 className="heading">{props.fields.Title.value}</h2>
          )}

          <div className="current-location">
            <div className="current-location-msg">
              <div className="icon icon-location">
                <Icons id="icon-pdc-current-location" />
              </div>

              <div className="osf-body-copy">
                Current Location: {location || 'Not set'}{' '}
                <span className="change-btn">
                  <span className="parenthesis"> (</span>

                  <Modal
                    customClass="modal pdc-modal"
                    openBtnLabel="Change"
                    portalId="pdc-modal-root"
                    shouldCloseOnOverlayClick={true}
                  >
                    <div className="icon icon-modal-triangle">
                      <Icons id="icon-modal-triangle" />
                    </div>

                    <div className="modal-details">
                      <h3>Set Your Location</h3>

                      <p>
                        Providing your location helps us connect you with nearby
                        care that's convenient for you.
                      </p>

                      <div className="location-filter">
                        <label>Location (City or Zip)</label>

                        <div className="location-distance-panel">
                          <div className="react-autosuggest__container input-icon-container">
                            <div className="input-wrapper">
                              <input
                                placeholder="Enter Location"
                                className="location-search-input"
                                disabled={false}
                                type="text"
                                value={inputLocation || ''}
                                onChange={(event) =>
                                  handleChange(event.target.value)
                                }
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    event.preventDefault();
                                    handleSelect();
                                  }
                                }}
                              />

                              <button
                                className="btn-primary no-icon"
                                onClick={() => handleSelect()}
                              >
                                Set
                              </button>
                            </div>

                            {inputLocation?.length > 1 &&
                              suggestions.length > 0 && (
                                <div className="react-autosuggest__suggestions-container">
                                  {suggestions.map((suggestion) => (
                                    <div
                                      className="typeahead-suggestion"
                                      key={suggestion.id}
                                      onMouseDown={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        handleSelect(suggestion);
                                      }}
                                    >
                                      <span className="suggestion-name">
                                        {suggestion.label
                                          .replace(',', '')
                                          .replace(/\d+/g, '')}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}

                            {inputLocation?.length > 1 &&
                              !isLoadingSuggestions &&
                              suggestions.length === 0 && (
                                <div className="react-autosuggest__suggestions-container">
                                  <div className="typeahead-suggestion">
                                    <span className="suggestion-name">
                                      No locations found
                                    </span>
                                  </div>
                                </div>
                              )}
                          </div>
                        </div>

                        <div className="current-location">
                          <div className="icon icon-distance">
                            <Icons id="icon-distance" />
                          </div>

                          <button onClick={() => useCurrentLoc()}>
                            Use my current location
                          </button>
                        </div>
                      </div>
                    </div>
                  </Modal>

                  <span className="parenthesis">)</span>
                </span>
              </div>
            </div>
          </div>

          <div id="pdc-modal-root"></div>
        </div>

        {geolocating && <p>Loading location...</p>}

        {coords && filteredLocations.length === 0 && locations.length > 0 && (
          <div className="no-results-message">
            <p>
              No locations found within{' '}
              {props.fields.Radius?.fields?.heading?.value || DEFAULT_DISTANCE}{' '}
              miles of {location}.
            </p>
          </div>
        )}

        {filteredLocations && filteredLocations.length > 0 && (
          <div className="locations-container">
            <div className="page-image">
              {props.fields['Page Image']?.value?.src ? (
                <Image
                  field={props.fields['Page Image']}
                  className="location-image"
                />
              ) : (
                <div className="image-placeholder">
                  <p className="alt-text-content">
                    {String(
                      props.fields['Page Image']?.value?.alt ||
                        'No image available'
                    )}
                  </p>
                </div>
              )}
            </div>

            <div className="locations-list">
              {locationsToDisplay.map((loc: any, index) => {
                const locationName =
                  loc.fields?.LocationName?.value ||
                  loc.displayName ||
                  loc.locationname ||
                  'Unknown Location';

                const lat = loc.fields?.Latitude?.value || loc.latitude;
                const lng = loc.fields?.Longitude?.value || loc.longitude;

                const locationId =
                  loc.fields?.LocationId?.value || loc.id || `loc-${index}`;

                const street = loc.fields?.Street1?.value || loc.street1;
                const city = loc.fields?.City?.value || loc.city;
                const state = loc.fields?.State?.value || loc.state;
                const phone = loc.fields?.Phone?.value || loc.phone;

                const distance =
                  coords && lat && lng
                    ? getDistance(
                        coords.lat,
                        coords.lng,
                        parseFloat(lat),
                        parseFloat(lng)
                      )
                    : null;

                return (
                  <div className="location-item" key={`location-${index}`}>
                    <h3 className="osf-display-h3 location-name">
                      <a href={formatLocationUrl(locationId, locationName)}>
                        {locationName}
                        <Icons
                          id="chevron-right-icon"
                          className="icon icon-chevron-right"
                          aria-hidden="true"
                        />
                      </a>
                    </h3>

                    <div className="osf-body-copy location-info">
                      {distance && (
                        <div className="info-row full-width">
                          <Icons id="icon-distance" />
                          <span>{distance.toFixed(1)} miles away</span>
                        </div>
                      )}

                      {waitTimes[locationId] && (
                        <div className="info-row">
                          <Icons id="person" />
                          <span>
                            <span className="label">Walk-in wait time:</span>
                            <span className="value">
                              {waitTimes[locationId]}
                            </span>
                          </span>
                        </div>
                      )}

                      {nextAppts[locationId] && (
                        <div className="info-row">
                          <span className="icon-calendar">
                            <Icons id="icon-calendarNew" />
                          </span>
                          <span>
                            <span className="label">Next appointment:</span>
                            <span className="value">
                              {nextAppts[locationId]}
                            </span>
                          </span>
                        </div>
                      )}

                      {street && (
                        <div className="info-row">
                          <Icons id="icon-pdc-location" />
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              `${street}${
                                city && state ? `, ${city}, ${state}` : ''
                              }`
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="address-link"
                          >
                            {street}
                            {city && state && (
                              <>
                                , {city}, {state}
                              </>
                            )}
                          </a>
                        </div>
                      )}

                      {phone && (
                        <div className="info-row">
                          <Icons id="phone" />
                          <a href={`tel:${phone}`} className="phone-link">
                            {phone}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {props.fields['View More CTA']?.value?.href && (
                <div className="see-more-container">
                  <a
                    href={props.fields['View More CTA'].value.href}
                    className="btn-primary btn-see-more"
                    target={
                      props.fields['View More CTA'].value.target || '_self'
                    }
                  >
                    {props.fields['View More CTA'].value.text ||
                      'See more results'}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationCallout;